import React, { useEffect, useRef, useState } from "react";
import {
  generateTopicDraftFromTranscript,
  getAiModel,
  getSttProvider,
  refineVideoTranscript,
  saveVideoTranscriptionTopic,
  transcribeYouTubeVideo,
} from "../api";

const DURATION_OPTIONS = [
  { value: 60, label: "1 minute" },
  { value: 300, label: "5 minutes" },
  { value: 600, label: "10 minutes" },
  { value: "full", label: "Full video (safe limit)" },
];

const AI_MODEL_OPTIONS = [
  { value: "gpt-5", label: "GPT-5" },
  { value: "gpt-5-mini", label: "GPT-5 Mini" },
  { value: "gpt-5-nano", label: "GPT-5 Nano" },
  { value: "gpt-4.1", label: "GPT-4.1" },
  { value: "gpt-4.1-mini", label: "GPT-4.1 Mini" },
  { value: "gpt-4o", label: "GPT-4o" },
  { value: "gpt-4o-mini", label: "GPT-4o Mini" },
  { value: "gpt-4-turbo", label: "GPT-4 Turbo" },
  { value: "gpt-4", label: "GPT-4" },
  { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
];

export default function VideoTranscriptionTopicCreator({
  onSaved,
  onCancel = null,
  isAdmin = true,
  maxDurationSeconds = null,
}) {
  const userCap = !isAdmin ? Math.max(1, Number(maxDurationSeconds) || 60) : null;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [topicOrder, setTopicOrder] = useState("");
  const [provider, setProvider] = useState("google");
  const [duration, setDuration] = useState(isAdmin ? 60 : (Math.max(1, Number(maxDurationSeconds) || 60)));
  const [refineCount, setRefineCount] = useState(0);
  const [customDuration, setCustomDuration] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [transcript, setTranscript] = useState("");
  const [transcribeMetadata, setTranscribeMetadata] = useState(null);
  const [draftImportContent, setDraftImportContent] = useState("");
  const [resultMsg, setResultMsg] = useState("");
  const [refineModel, setRefineModel] = useState("gpt-4o-mini");
  const [refinedScenario, setRefinedScenario] = useState(null);
  const [savedYoutubeUrl, setSavedYoutubeUrl] = useState("");
  // Video topics always show their full vocab; the backend no longer dedupes.
  const includeDuplicates = false;
  const [startTime, setStartTime] = useState("0:00");
  const [splitIntoMinutes, setSplitIntoMinutes] = useState(false);
  const [splitProgress, setSplitProgress] = useState(null);
  const [splitResults, setSplitResults] = useState([]);
  const successBannerRef = useRef(null);

  // Parse "mm:ss" or "h:mm:ss" or plain seconds → integer seconds (or NaN if invalid).
  function parseTimeToSeconds(value) {
    const v = String(value || "").trim();
    if (!v) return 0;
    if (!v.includes(":")) {
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 ? Math.floor(n) : NaN;
    }
    const parts = v.split(":").map((p) => p.trim());
    if (parts.some((p) => p === "" || !/^\d+$/.test(p))) return NaN;
    const nums = parts.map((p) => Number(p));
    if (nums.some((n) => !Number.isFinite(n) || n < 0)) return NaN;
    let total = 0;
    for (const n of nums) total = total * 60 + n;
    return total;
  }

  const startSeconds = parseTimeToSeconds(startTime);
  const startSecondsValid = Number.isFinite(startSeconds) && startSeconds >= 0;

  useEffect(() => {
    // Provider/model pickers are admin-only; non-admins use the server defaults.
    if (!isAdmin) return;
    getSttProvider()
      .then((data) => {
        if (data?.provider) setProvider(data.provider);
      })
      .catch(() => {});
    getAiModel()
      .then((data) => {
        if (data?.model) setRefineModel(data.model);
      })
      .catch(() => {});
  }, [isAdmin]);

  const effectiveDuration = duration === "custom" ? Number(customDuration || 0) : duration;

  // For split mode: total seconds we'll process. "full" is treated as ineligible
  // for splitting (we don't know the video length client-side until after a probe).
  const splitTotalSeconds = duration === "full" ? 0 : Number(effectiveDuration || 0);
  const splitEligible = splitTotalSeconds > 60;
  const splitPartCount = splitEligible ? Math.ceil(splitTotalSeconds / 60) : 0;
  const splitActive = splitIntoMinutes && splitEligible;

  async function handleSplitTranscribeAndSave() {
    if (!title.trim() || !youtubeUrl.trim() || !startSecondsValid) return;
    setStatus("splitting");
    setError("");
    setResultMsg("");
    setSplitResults([]);
    setSavedYoutubeUrl("");

    const baseStart = startSecondsValid ? startSeconds : 0;
    const baseOrder = topicOrder ? Number(topicOrder) : null;
    const total = splitPartCount;
    const results = [];

    try {
      for (let i = 0; i < total; i += 1) {
        const segStart = baseStart + i * 60;
        const remaining = splitTotalSeconds - i * 60;
        const segDuration = Math.min(60, remaining);
        const partLabel = `Part ${i + 1}`;
        const partTitle = `${title.trim()} (${partLabel})`;

        setSplitProgress({ current: i + 1, total, message: `Transcribing ${partLabel}…` });
        const tx = await transcribeYouTubeVideo({
          youtubeUrl,
          provider,
          durationSeconds: segDuration,
          startSeconds: segStart,
        });
        const segTranscript = (tx.transcript || "").trim();
        if (!segTranscript) {
          throw new Error(`${partLabel}: empty transcript from STT.`);
        }
        const segMeta = tx.metadata || {};

        setSplitProgress({ current: i + 1, total, message: `Generating draft for ${partLabel}…` });
        const draft = await generateTopicDraftFromTranscript({
          title: partTitle,
          transcript: segTranscript,
          description,
          order: baseOrder !== null ? baseOrder + i : null,
          scenario: null,
        });

        setSplitProgress({ current: i + 1, total, message: `Saving ${partLabel}…` });
        const saved = await saveVideoTranscriptionTopic({
          importContent: draft.import_content || "",
          youtubeUrl,
          provider,
          durationSeconds: segMeta.duration_seconds || segDuration,
          startSeconds: segMeta.start_seconds ?? segStart,
          transcriptText: segTranscript,
          includeDuplicates,
        });
        results.push({
          part: i + 1,
          name: saved.topic_name,
          url: saved.youtube_url_at_start || saved.youtube_url || youtubeUrl,
        });
        setSplitResults([...results]);
        if (onSaved) onSaved(saved);
      }

      setSplitProgress(null);
      setStatus("saved");
      setResultMsg(`Saved ${results.length} topic${results.length === 1 ? "" : "s"}.`);
      setSavedYoutubeUrl(results[0]?.url || "");
      setTimeout(() => {
        if (successBannerRef.current) {
          successBannerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 50);
    } catch (e) {
      setSplitProgress(null);
      setError(
        (e && e.message ? e.message : "Split save failed.") +
          (results.length ? ` (${results.length} of ${total} parts saved before error.)` : "")
      );
      setStatus("error");
    }
  }

  async function handleTranscribe() {
    setStatus("extracting");
    setError("");
    setResultMsg("");
    try {
      const res = await transcribeYouTubeVideo({
        youtubeUrl,
        provider,
        durationSeconds: duration === "full" ? "full" : effectiveDuration,
        startSeconds: startSecondsValid ? startSeconds : 0,
      });
      setTranscript(res.transcript || "");
      setTranscribeMetadata(res.metadata || null);
      setStatus("transcript_ready");
    } catch (e) {
      setError(e.message || "Transcription failed.");
      setStatus("error");
    }
  }

  async function handleRefineTranscript() {
    if (!transcript.trim()) return;
    setStatus("refining");
    setError("");
    setResultMsg("");
    try {
      const res = await refineVideoTranscript({
        transcript,
        model: refineModel,
      });
      if (res?.refined_transcript) {
        setTranscript(res.refined_transcript);
      }
      setRefineCount((n) => n + 1);
      const sentences = Array.isArray(res?.sentences) ? res.sentences : [];
      setRefinedScenario(sentences.length ? sentences : null);
      setResultMsg(
        `Transcript refined with ${res?.model || refineModel}` +
        (sentences.length ? ` — ${sentences.length} scenario lines with translit + translation.` : ".")
      );
      setStatus("transcript_ready");
    } catch (e) {
      setError(e.message || "Refine failed.");
      setStatus("error");
    }
  }

  async function handleGenerateDraft() {
    setStatus("generating");
    setError("");
    setResultMsg("");
    try {
      const res = await generateTopicDraftFromTranscript({
        title,
        transcript,
        description,
        order: topicOrder ? Number(topicOrder) : null,
        scenario: refinedScenario,
      });
      setDraftImportContent(res.import_content || "");
      setStatus("draft_ready");
    } catch (e) {
      setError(e.message || "Draft generation failed.");
      setStatus("error");
    }
  }

  async function handleSave() {
    setStatus("saving");
    setError("");
    setResultMsg("");
    try {
      const res = await saveVideoTranscriptionTopic({
        importContent: draftImportContent,
        youtubeUrl,
        provider,
        durationSeconds:
          transcribeMetadata?.duration_seconds ||
          (duration === "full" ? 3600 : effectiveDuration),
        startSeconds:
          transcribeMetadata?.start_seconds ?? (startSecondsValid ? startSeconds : 0),
        transcriptText: transcript,
        includeDuplicates,
      });
      setStatus("saved");
      setResultMsg(`Saved topic "${res.topic_name}"`);
      setSavedYoutubeUrl(res.youtube_url_at_start || res.youtube_url || youtubeUrl || "");
      setTimeout(() => {
        if (successBannerRef.current) {
          successBannerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 50);
      if (onSaved) onSaved(res);
    } catch (e) {
      setError(e.message || "Save failed.");
      setStatus("error");
    }
  }

  // Simplified one-click flow (non-admin): build the draft and save it in a
  // single step, using the (optionally refined) transcript as-is.
  async function handleCreateTopic() {
    if (!transcript.trim()) return;
    setError("");
    setResultMsg("");
    try {
      setStatus("generating");
      const draft = await generateTopicDraftFromTranscript({
        title,
        transcript,
        description,
        order: null,
        scenario: refinedScenario,
      });
      setStatus("saving");
      const res = await saveVideoTranscriptionTopic({
        importContent: draft.import_content || "",
        youtubeUrl,
        provider: transcribeMetadata?.provider || provider,
        durationSeconds: transcribeMetadata?.duration_seconds || effectiveDuration,
        startSeconds: transcribeMetadata?.start_seconds ?? (startSecondsValid ? startSeconds : 0),
        transcriptText: transcript,
        includeDuplicates: false,
      });
      setStatus("saved");
      setResultMsg(`Created topic "${res.topic_name}"`);
      setSavedYoutubeUrl(res.youtube_url_at_start || res.youtube_url || youtubeUrl || "");
      setTimeout(() => {
        successBannerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
      if (onSaved) onSaved(res);
    } catch (e) {
      setError(e.message || "Could not create topic.");
      setStatus("error");
    }
  }

  // ── Simplified flow for normal users ──────────────────────────────────────
  if (!isAdmin) {
    const minutesLabel =
      userCap % 60 === 0 ? `${userCap / 60} min` : `${userCap} sec`;
    const transcriptReady = transcript.trim() && status !== "extracting";
    const busy = status === "extracting" || status === "refining" || status === "generating" || status === "saving";
    return (
      <div className="space-y-4">
        {status === "saved" && (
          <div
            ref={successBannerRef}
            className="rounded-lg border-2 border-emerald-300 bg-emerald-50 px-4 py-3 shadow-sm"
          >
            <div className="text-sm font-semibold text-emerald-900">✅ {resultMsg || "Topic created!"}</div>
            <div className="text-sm text-emerald-900 mt-1">
              Find it in your topics list. Open the Listening step to watch the video with the synced transcript.
            </div>
          </div>
        )}

        <div className="grid gap-3">
          <label className="text-sm text-slate-700">
            <span className="block mb-1 font-medium">Topic title</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
              placeholder="e.g. Travel Conversation"
            />
          </label>
          <label className="text-sm text-slate-700">
            <span className="block mb-1 font-medium">YouTube URL</span>
            <input
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
              placeholder="https://www.youtube.com/watch?v=..."
            />
          </label>
          <label className="text-sm text-slate-700">
            <span className="block mb-1 font-medium">Start time (optional)</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="0:00 (mm:ss)"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border text-sm ${
                startSecondsValid ? "border-slate-200" : "border-rose-400"
              }`}
            />
            <span className="block mt-1 text-xs text-slate-500">
              Where to start in the video. Up to {minutesLabel} will be transcribed.
            </span>
          </label>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleTranscribe}
            disabled={!title.trim() || !youtubeUrl.trim() || !startSecondsValid || busy}
            className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50"
          >
            {status === "extracting" ? "Transcribing…" : transcript.trim() ? "Re-transcribe" : "Transcribe"}
          </button>
          <button
            onClick={handleCreateTopic}
            disabled={!transcriptReady || busy}
            className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50"
          >
            {status === "generating" || status === "saving" ? "Creating…" : "Create Topic"}
          </button>
          {onCancel && (
            <button
              onClick={onCancel}
              className="px-3 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm"
            >
              Cancel
            </button>
          )}
        </div>

        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

        {transcript.trim() && status !== "saved" && (
          <div>
            <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
              <label className="block text-sm font-medium text-slate-700">Transcript (you can edit it)</label>
              <button
                onClick={handleRefineTranscript}
                disabled={!transcript.trim() || status === "refining" || refineCount >= 1}
                className="px-2.5 py-1 rounded-lg bg-amber-600 text-white text-xs font-medium disabled:opacity-50"
                title={refineCount >= 1 ? "Already refined once" : "Add harakat and fix spelling (once)"}
              >
                {status === "refining" ? "Refining…" : refineCount >= 1 ? "Refined ✓" : "Refine (harakat + spelling)"}
              </button>
            </div>
            <textarea
              value={transcript}
              onChange={(e) => { setTranscript(e.target.value); setRefinedScenario(null); }}
              dir="rtl"
              lang="ar"
              className="w-full min-h-[120px] px-3 py-2 rounded-lg border border-slate-200 text-sm font-arabic"
              placeholder="Transcript will appear here after Transcribe."
            />
            <span className="block mt-1 text-xs text-slate-500">
              Optionally tidy the text or Refine once, then press Create Topic.
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {status === "saved" && savedYoutubeUrl && (
        <div
          ref={successBannerRef}
          className="rounded-lg border-2 border-emerald-300 bg-emerald-50 px-4 py-3 shadow-sm"
        >
          <div className="text-sm font-semibold text-emerald-900 mb-1">
            ✅ Topic saved successfully!
          </div>
          <div className="text-sm text-emerald-900 mb-2">
            Open the source YouTube video at the scenario start position:
          </div>
          <a
            href={savedYoutubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700"
          >
            ▶ Open on YouTube
          </a>
          <div className="mt-2 text-xs text-emerald-800 break-all">
            <a href={savedYoutubeUrl} target="_blank" rel="noopener noreferrer" className="underline">
              {savedYoutubeUrl}
            </a>
          </div>
        </div>
      )}

      <div className="grid gap-3">
        <label className="text-sm text-slate-700">
          <span className="block mb-1 font-medium">Topic title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
            placeholder="e.g. Video: Travel Conversation"
          />
        </label>
        <label className="text-sm text-slate-700">
          <span className="block mb-1 font-medium">Description (optional)</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
            placeholder="Optional topic description"
          />
        </label>
        <label className="text-sm text-slate-700">
          <span className="block mb-1 font-medium">Topic order (optional)</span>
          <input
            type="number"
            min="1"
            value={topicOrder}
            onChange={(e) => setTopicOrder(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
            placeholder="Auto-append if empty"
          />
        </label>
        <label className="text-sm text-slate-700">
          <span className="block mb-1 font-medium">YouTube URL</span>
          <input
            value={youtubeUrl}
            onChange={(e) => setYoutubeUrl(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
            placeholder="https://www.youtube.com/watch?v=..."
          />
        </label>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm text-slate-700">
            <span className="block mb-1 font-medium">Speech-to-text model</span>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
            >
              <option value="openai">OpenAI Whisper</option>
              <option value="google">Google Cloud Speech-to-Text</option>
            </select>
          </label>
          <label className="text-sm text-slate-700">
            <span className="block mb-1 font-medium">Duration to transcribe</span>
            <select
              value={duration}
              onChange={(e) => setDuration(e.target.value === "custom" || e.target.value === "full" ? e.target.value : Number(e.target.value))}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
            >
              {DURATION_OPTIONS.map((o) => (
                <option key={String(o.value)} value={o.value}>{o.label}</option>
              ))}
              <option value="custom">Custom</option>
            </select>
          </label>
        </div>
        {duration === "custom" && (
          <label className="text-sm text-slate-700">
            <span className="block mb-1 font-medium">Custom duration (seconds)</span>
            <input
              type="number"
              min="1"
              max="3600"
              value={customDuration}
              onChange={(e) => setCustomDuration(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
            />
          </label>
        )}
        <label className="text-sm text-slate-700">
          <span className="block mb-1 font-medium">Start time</span>
          <input
            type="text"
            inputMode="numeric"
            placeholder="0:00 (mm:ss or h:mm:ss)"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className={`w-full px-3 py-2 rounded-lg border text-sm ${
              startSecondsValid ? "border-slate-200" : "border-rose-400"
            }`}
          />
          <span className="block mt-1 text-xs text-slate-500">
            Seek into the video before transcribing. Scenario timestamp will start here.
          </span>
        </label>
        {splitEligible && (
          <label className="flex items-start gap-2 text-sm text-slate-700 select-none">
            <input
              type="checkbox"
              checked={splitIntoMinutes}
              onChange={(e) => setSplitIntoMinutes(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium">Split into 1-minute topics</span>{" "}
              <span className="text-slate-500">
                ({splitPartCount} topic{splitPartCount === 1 ? "" : "s"} —
                {" "}
                last {splitTotalSeconds % 60 === 0 ? "is also 60s" : `is ${splitTotalSeconds % 60}s`}).
                Each part is transcribed and saved automatically as
                {" "}
                "<span className="font-mono">{(title.trim() || "Topic")} (Part 1)</span>", "(Part 2)", …
                Refine and manual review are skipped in split mode.
              </span>
            </span>
          </label>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {splitActive ? (
          <button
            onClick={handleSplitTranscribeAndSave}
            disabled={
              !title.trim() ||
              !youtubeUrl.trim() ||
              !startSecondsValid ||
              status === "splitting"
            }
            className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50"
          >
            {status === "splitting"
              ? `Processing ${splitProgress?.current || 0}/${splitProgress?.total || splitPartCount}…`
              : `Transcribe & save as ${splitPartCount} topics`}
          </button>
        ) : (
          <>
            <button
              onClick={handleTranscribe}
              disabled={
                !title.trim() ||
                !youtubeUrl.trim() ||
                (duration === "custom" && !effectiveDuration) ||
                !startSecondsValid
              }
              className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50"
            >
              Transcribe
            </button>
            <button
              onClick={handleGenerateDraft}
              disabled={!transcript.trim()}
              className="px-3 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium disabled:opacity-50"
            >
              Generate Topic Draft
            </button>
            <button
              onClick={handleSave}
              disabled={!draftImportContent.trim()}
              className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50"
            >
              Save Topic
            </button>
          </>
        )}
        {onCancel && (
          <button
            onClick={onCancel}
            className="px-3 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm"
          >
            Cancel
          </button>
        )}
      </div>

      <div className="text-xs text-slate-500">
        Status: {
          status === "extracting" ? "extracting audio" :
          status === "transcript_ready" ? "transcript ready" :
          status === "refining" ? "refining transcript with AI" :
          status === "generating" ? "generating topic draft" :
          status === "draft_ready" ? "draft ready" :
          status === "saving" ? "saving topic" :
          status === "splitting" ? `splitting (${splitProgress?.current || 0}/${splitProgress?.total || splitPartCount})` :
          status === "saved" ? "saved" :
          status === "error" ? "error" : "idle"
        }
      </div>

      {splitProgress && (
        <div className="text-sm text-slate-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
          {splitProgress.message} ({splitProgress.current} of {splitProgress.total})
        </div>
      )}
      {splitResults.length > 0 && (
        <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 space-y-1">
          <div className="font-medium">Saved {splitResults.length} topic{splitResults.length === 1 ? "" : "s"}:</div>
          <ul className="list-disc pl-5">
            {splitResults.map((r) => (
              <li key={r.part}>
                Part {r.part}: <span className="font-medium">{r.name}</span>
                {r.url && (
                  <>
                    {" — "}
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="underline">open on YouTube</a>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
      {resultMsg && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">{resultMsg}</div>}

      {!splitActive && (
        <>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
              <label className="block text-sm font-medium text-slate-700">Transcript (editable)</label>
              <div className="flex items-center gap-2">
                <select
                  value={refineModel}
                  onChange={(e) => setRefineModel(e.target.value)}
                  className="px-2 py-1 rounded-lg border border-slate-200 text-xs bg-white"
                  title="OpenAI model used for harakat + spelling refinement"
                >
                  {AI_MODEL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <button
                  onClick={handleRefineTranscript}
                  disabled={!transcript.trim() || status === "refining"}
                  className="px-2.5 py-1 rounded-lg bg-amber-600 text-white text-xs font-medium disabled:opacity-50"
                  title="Add harakat and fix spelling using OpenAI"
                >
                  {status === "refining" ? "Refining…" : "Refine (harakat + spelling)"}
                </button>
              </div>
            </div>
            <textarea
              value={transcript}
              onChange={(e) => { setTranscript(e.target.value); setRefinedScenario(null); }}
              dir="rtl"
              lang="ar"
              className="w-full min-h-[120px] px-3 py-2 rounded-lg border border-slate-200 text-sm font-arabic"
              placeholder="Transcript will appear here after Transcribe."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Topic draft (editable import format)</label>
            <textarea
              value={draftImportContent}
              onChange={(e) => setDraftImportContent(e.target.value)}
              className="w-full min-h-[200px] px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono"
              placeholder="Generated draft content appears here."
            />
          </div>
        </>
      )}
    </div>
  );
}
